export enum IframeMessageType {
  MODAL_OPEN = 'MODAL_OPEN',
  MODAL_CLOSE = 'MODAL_CLOSE',
  IMAGE_SELECTED = 'IMAGE_SELECTED',
  CONTENT_LOADED = 'CONTENT_LOADED',
}

export enum ModalType {
  AI_IMPACT = 'AI_IMPACT',
  ONBOARDING = 'ONBOARDING',
  PATIENT_EDUCATION = 'PATIENT_EDUCATION',
  PRINT_DOWNLOAD = 'PRINT_DOWNLOAD',
  QR_CODE = 'QR_CODE',
  UPGRADE = 'UPGRADE',
}

export interface ModalOpenPayload {
  type: string;
  externalPatientId: string | null;
  externalImageId: string | null;
  patientId: string | null;
  imageId: string | null;
}

// Message schema types for RCI to VideaAI iFrame communication
export interface ModalOpenMessage {
  type: string;
  payload: ModalOpenPayload;
}

export interface ImageSelectedMessage {
  type: string;
  payload: {
    imageId: string;
  };
}

export interface ModalCloseMessage {
  type: string;
  payload?: {
    type?: string;
  };
}

export interface ExternalPatientContextType {
  isIframe: boolean;
  externalPatientId: string | null;
  externalImageId: string | null;
  patientId: string | null;
  imageId: string | null;
  trialStatus: TrialStatus | null;
  centralizedBilling: boolean | null;
}

export enum TrialStatus {
  CANCEL_REQUEST = 'CANCEL_REQUEST', // Only used in mutation to either cancel free trial or request cancellation of paid subscription
  CANCEL_REQUEST_PAID = 'CANCEL_REQUEST_PAID',
  CANCEL_REQUEST_PROVISIONAL = 'CANCEL_REQUEST_PROVISIONAL', // User requested cancellation but Salesforce hasn't ceased billing, returned in get request
  CANCEL_REQUEST_TRIAL = 'CANCEL_REQUEST_TRIAL', // Free trial was cancelled, returned in get request
  PAID = 'PAID', // Paid subscription
  PAID_BUT_PROVISIONAL = 'PAID_BUT_PROVISIONAL', // Sent to BE when upgrading from trial
  TRIAL = 'TRIAL', // Free trial
  TRIAL_ELIGIBLE = 'TRIAL_ELIGIBLE', // Free trial offered
  TRIAL_EXPIRED = 'TRIAL_EXPIRED', // Free trial expired
}

export interface TrialStatusData {
  centralizedBilling?: boolean;
  createdAt: string;
  createdBy: string;
  externalPracticeId: string;
  isReverseTrialPractice?: boolean;
  paidEndedOn: string;
  paidEndsOn?: string;
  paidStartedOn: string;
  practiceId: number;
  status: TrialStatus;
  trialEndedOn: string;
  trialEndsOn: string;
  trialLengthInDays?: number;
  trialStartedOn: string;
  updatedAt: string;
  updatedBy?: string;
  videaPracticeId: string;
}

export type CancelCheckboxReason =
  | 'MISSING_FEATURES'
  | 'NOT_USING_ENOUGH'
  | 'DIFFICULT_TO_USE'
  | 'COST'
  | 'SWITCHING_TO_OTHER_PRODUCT'
  | 'OTHER';

export interface PracticeAIImpactDataResponse {
  // Patient education
  totalUniquePatients?: string;
  uniquePatientsEducatedWithAi?: string;
  uniquePatientsWithRecs?: string;

  // Case acceptance
  uniqueCompletedTxWithRec?: string;
  uniquePlannedTxWithRec?: string;
  uniqueScheduledTxWithRec?: string;

  // Treatment planning
  avgTxPlanValuePerPatientPostAi?: string;
  avgTxPlanValuePerPatientPreAi?: string;
  highlightCategory?: string;
  crownPostAiValue?: string;
  crownPreAiValue?: string;
  extractionPostAiValue?: string;
  extractionPreAiValue?: string;
  fillingPostAiValue?: string;
  fillingPreAiValue?: string;
  implantPostAiValue?: string;
  implantPreAiValue?: string;
  incipientPostAiValue?: string;
  incipientPreAiValue?: string;
  rootCanalPostAiValue?: string;
  rootCanalPreAiValue?: string;
  srpPostAiValue?: string;
  srpPreAiValue?: string;
}

export type HighlightCategory =
  | 'Crowns'
  | 'Extraction'
  | 'Fillings'
  | 'Implant'
  | 'Incipient'
  | 'Root Canal'
  | 'SRP';

export interface PracticeAIImpactData {
  // Patient education
  totalUniquePatients?: number;
  uniquePatientsEducatedWithAi?: number;
  uniquePatientsWithRecs?: number;

  // Case acceptance
  uniqueCompletedTxWithRec?: number;
  uniquePlannedTxWithRec?: number;
  uniqueScheduledTxWithRec?: number;

  // Treatment planning
  avgTxPlanValuePerPatientPostAi?: number;
  avgTxPlanValuePerPatientPreAi?: number;
  highlightCategory?: HighlightCategory;
  crownPostAiValue?: number;
  crownPreAiValue?: number;
  extractionPostAiValue?: number;
  extractionPreAiValue?: number;
  fillingPostAiValue?: number;
  fillingPreAiValue?: number;
  implantPostAiValue?: number;
  implantPreAiValue?: number;
  incipientPostAiValue?: number;
  incipientPreAiValue?: number;
  rootCanalPostAiValue?: number;
  rootCanalPreAiValue?: number;
  srpPostAiValue?: number;
  srpPreAiValue?: number;
}
